import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronUp, Pencil, Plane, ShieldCheck } from "lucide-react";
import { useCalendarEvents } from "@/hooks/use-calendar-events";
import type { CalendarEvent, CalendarKind } from "@/hooks/use-calendar-events";
import { useDocuments, type DocumentRow } from "@/hooks/use-documents";
import { formatShortDate } from "@/lib/date-format";
import { WeatherCard } from "@/components/today/WeatherCard";
import { EditMealDialog } from "@/components/today/EditMealDialog";
import { CalendarToolbar, type CalendarView } from "@/components/calendar/CalendarToolbar";
import { NewEventModal } from "@/components/calendar/NewEventModal";
import { addDays, isoDate, isoMonth, isoWeek, isoYear } from "@/lib/calendar-utils";

const VIEW: CalendarView = "day";

const SECTIONS: Array<{ kind: CalendarKind; label: string }> = [
  { kind: "user_event", label: "Events" },
  { kind: "external", label: "Calendar events" },
  { kind: "session", label: "Workouts" },
  { kind: "meal", label: "Meals" },
  { kind: "body", label: "Body" },
  { kind: "supplement", label: "Supplements" },
  { kind: "recovery", label: "Recovery" },
  { kind: "mood", label: "Mood / check-ins" },
  { kind: "project_deadline", label: "Project" },
];

export function CalendarDay() {
  const params = useParams<{ date: string }>();
  const navigate = useNavigate();
  const dateParam = params.date === "today" ? isoToday() : params.date ?? isoToday();

  const dayDate = useMemo(() => new Date(`${dateParam}T00:00:00`), [dateParam]);
  const { rangeStart, rangeEnd } = useMemo(() => {
    const start = new Date(`${dateParam}T00:00:00`);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { rangeStart: start, rangeEnd: end };
  }, [dateParam]);

  const events = useCalendarEvents(rangeStart, rangeEnd);
  const docsQ = useDocuments();

  const grouped = useMemo(() => {
    const map = new Map<CalendarKind, CalendarEvent[]>();
    for (const e of events.data ?? []) {
      const list = map.get(e.kind) ?? [];
      list.push(e);
      map.set(e.kind, list);
    }
    return map;
  }, [events.data]);

  // Travel mode: surface relevant docs when the day has a flight / train /
  // boarding event. Identity docs (passport, ID) always show on travel
  // days; travel docs (visa) show; tickets matching this date show.
  const travelMode = useMemo(
    () => detectTravel(events.data ?? [], docsQ.data ?? [], dateParam),
    [events.data, docsQ.data, dateParam],
  );

  const [showNew, setShowNew] = useState(false);
  const [editingMealId, setEditingMealId] = useState<string | null>(null);

  function go(delta: number) {
    navigate(`/calendar/day/${isoDate(addDays(dayDate, delta))}`);
  }

  return (
    <article aria-labelledby="calendar-day-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Calendar · Day
      </p>
      <CalendarToolbar
        title={formatShortDate(dateParam)}
        view={VIEW}
        isoDay={dateParam}
        isoWeek={isoWeek(dayDate)}
        isoMonth={isoMonth(dayDate)}
        isoYear={isoYear(dayDate)}
        onPrev={() => go(-1)}
        onNext={() => go(1)}
        onToday={() => navigate(`/calendar/day/${isoToday()}`)}
        onNewEvent={() => setShowNew(true)}
      />
      <h2 id="calendar-day-heading" style={{ position: "absolute", left: -9999 }}>
        Day detail
      </h2>

      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        {travelMode.active && travelMode.docs.length > 0 && (
          <TravelDocsCard reason={travelMode.reason} docs={travelMode.docs} dateParam={dateParam} />
        )}
        <WeatherCard />
        {SECTIONS.map(({ kind, label }) => (
          <Section
            key={kind}
            label={label}
            count={grouped.get(kind)?.length ?? 0}
            events={grouped.get(kind) ?? []}
            onMealClick={(mealId) => setEditingMealId(mealId)}
          />
        ))}
      </div>

      <NewEventModal
        open={showNew}
        defaultStart={dayDate}
        onClose={() => setShowNew(false)}
      />
      <EditMealDialog
        mealId={editingMealId}
        open={editingMealId !== null}
        onClose={() => setEditingMealId(null)}
      />
    </article>
  );
}

function Section({
  label,
  count,
  events,
  onMealClick,
}: {
  label: string;
  count: number;
  events: CalendarEvent[];
  onMealClick?: (mealId: string) => void;
}) {
  const [open, setOpen] = useState(count > 0);
  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "transparent",
          border: "none",
          color: "var(--fg)",
          padding: "var(--space-3) var(--space-4)",
          cursor: "pointer",
          fontSize: "var(--type-body-s)",
        }}
      >
        <span style={{ fontWeight: 500 }}>
          {label}
          <span style={{ color: "var(--fg-3)", marginLeft: 6 }}>{count}</span>
        </span>
        {open ? <ChevronUp size={16} strokeWidth={1.5} /> : <ChevronDown size={16} strokeWidth={1.5} />}
      </button>
      {open && (
        <div style={{ padding: "0 var(--space-4) var(--space-3)" }}>
          {events.length === 0 ? (
            <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)", margin: 0 }}>
              Nothing logged.
            </p>
          ) : (
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
              {events.map((e) => (
                <EventRow
                  key={e.id}
                  event={e}
                  onMealClick={onMealClick}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * One row in the Day-view section list. Meal rows are buttons that open the
 * EditMealDialog for the underlying `meal.id` (HOTFIX-BW19.2 Bug 7) — without
 * this, a meal logged yesterday could not be edited from Calendar at all.
 * Other event kinds remain non-interactive in v1.
 */
function EventRow({
  event,
  onMealClick,
}: {
  event: CalendarEvent;
  onMealClick?: (mealId: string) => void;
}) {
  const isMeal = event.kind === "meal";
  const mealId = isMeal && event.id.startsWith("meal:") ? event.id.slice("meal:".length) : null;
  const interactive = isMeal && Boolean(mealId) && Boolean(onMealClick);

  const time = event.ts
    ? new Date(event.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "—";

  const innerColumns = interactive ? "auto 1fr auto auto" : "auto 1fr auto";

  const content = (
    <>
      <span className="mono" style={{ color: "var(--fg-3)" }}>
        {time}
      </span>
      <span style={{ color: "var(--fg)" }}>{event.title}</span>
      <span className="mono" style={{ color: "var(--fg-3)" }}>
        {event.detail ?? ""}
      </span>
      {interactive && <Pencil size={12} strokeWidth={1.5} color="var(--fg-3)" />}
    </>
  );

  if (interactive && mealId) {
    return (
      <li>
        <button
          type="button"
          onClick={() => onMealClick?.(mealId)}
          aria-label={`Edit ${event.title || "meal"}`}
          style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: innerColumns,
            gap: "var(--space-3)",
            alignItems: "baseline",
            fontSize: "var(--type-body-s)",
            color: "var(--fg-2)",
            padding: "4px 0",
            borderBottom: "1px solid var(--hair)",
            background: "transparent",
            border: "none",
            borderBottomColor: "var(--hair)",
            borderBottomStyle: "solid",
            borderBottomWidth: 1,
            textAlign: "left",
            cursor: "pointer",
            font: "inherit",
          }}
        >
          {content}
        </button>
      </li>
    );
  }

  return (
    <li
      style={{
        display: "grid",
        gridTemplateColumns: innerColumns,
        gap: "var(--space-3)",
        alignItems: "baseline",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
        padding: "4px 0",
        borderBottom: "1px solid var(--hair)",
      }}
    >
      {content}
    </li>
  );
}

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const TRAVEL_KEYWORDS = [
  "flight",
  "flug",
  "vol",
  "boarding",
  "departure",
  "arrival",
  "train",
  "ice",
  "tgv",
  "eurostar",
  "sbb",
  "ryanair",
  "lufthansa",
  "swiss",
  "klm",
  "air ",
  "airlines",
  "airport",
];

function detectTravel(
  events: CalendarEvent[],
  docs: DocumentRow[],
  dateIso: string,
): { active: boolean; reason: string | null; docs: DocumentRow[] } {
  // Find any event whose title hints at a flight / train / boarding pass.
  let hit: CalendarEvent | null = null;
  for (const e of events) {
    const t = (e.title ?? "").toLowerCase();
    if (TRAVEL_KEYWORDS.some((k) => t.includes(k))) {
      hit = e;
      break;
    }
  }
  if (!hit) return { active: false, reason: null, docs: [] };

  const filtered: DocumentRow[] = [];
  for (const d of docs) {
    if (d.category === "identity") {
      filtered.push(d);
      continue;
    }
    if (d.category === "travel") {
      filtered.push(d);
      continue;
    }
    // Tickets: only when their `valid_from`/issued_at/related metadata
    // coincide with the day or no date constraint is set.
    if (d.category === "ticket") {
      const meta = (d.metadata ?? {}) as Record<string, unknown>;
      const ticketDate = (meta.travel_date ?? d.issued_at ?? "") as string;
      if (!ticketDate || ticketDate.startsWith(dateIso)) {
        filtered.push(d);
      }
    }
  }

  return {
    active: true,
    reason: hit.title,
    docs: filtered.sort((a, b) => orderCategory(a.category) - orderCategory(b.category)),
  };
}

function orderCategory(c: string): number {
  const order = ["ticket", "identity", "travel", "membership"];
  const idx = order.indexOf(c);
  return idx === -1 ? 99 : idx;
}

function TravelDocsCard({
  reason,
  docs,
  dateParam,
}: {
  reason: string | null;
  docs: DocumentRow[];
  dateParam: string;
}) {
  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--accent-dim)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-4)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          marginBottom: "var(--space-3)",
        }}
      >
        <Plane size={16} strokeWidth={1.5} color="var(--accent)" />
        <span
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: "var(--type-body)",
            color: "var(--fg)",
          }}
        >
          Travel documents
        </span>
        {reason && (
          <span
            style={{
              color: "var(--fg-3)",
              fontSize: "var(--type-meta)",
              marginLeft: "var(--space-2)",
            }}
          >
            · for {reason}
          </span>
        )}
      </header>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: "var(--space-2)" }}>
        {docs.map((d) => (
          <li key={d.id}>
            <TravelDocRow doc={d} dateParam={dateParam} />
          </li>
        ))}
      </ul>
    </article>
  );
}

function TravelDocRow({ doc, dateParam }: { doc: DocumentRow; dateParam: string }) {
  const expiry = doc.expires_at ? new Date(doc.expires_at) : null;
  const day = new Date(`${dateParam}T00:00:00`);
  const expired = expiry && expiry.getTime() < day.getTime();
  const expiringSoon =
    expiry && !expired && expiry.getTime() - day.getTime() < 90 * 86_400_000;

  return (
    <Link
      to={`/documents/${doc.id}`}
      style={{
        display: "grid",
        gridTemplateColumns: "max-content 1fr max-content",
        gap: "var(--space-3)",
        padding: "var(--space-3)",
        background: "var(--bg-3)",
        border: `1px solid ${expired ? "var(--brick)" : expiringSoon ? "var(--amber)" : "var(--hair)"}`,
        borderRadius: "var(--radius-card)",
        textDecoration: "none",
        color: "var(--fg)",
        alignItems: "center",
      }}
    >
      <ShieldCheck size={16} strokeWidth={1.5} color="var(--fg-3)" />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "var(--type-body-s)", fontWeight: 500 }}>{doc.title}</div>
        <div style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
          {doc.kind.replace(/_/g, " ")}
          {doc.issuer && <> · {doc.issuer}</>}
        </div>
      </div>
      <div style={{ fontSize: "var(--type-meta)", color: expired ? "var(--brick)" : expiringSoon ? "var(--amber)" : "var(--fg-3)" }}>
        {expired
          ? `expired ${formatShortDate(doc.expires_at!)}`
          : expiringSoon
            ? `expires ${formatShortDate(doc.expires_at!)}`
            : doc.expires_at
              ? `valid to ${formatShortDate(doc.expires_at)}`
              : ""}
      </div>
    </Link>
  );
}
